import React, { useState, useEffect, useRef } from 'react';
import './Hero.css';

const Counter = ({ endValue, suffix = '', isVisible }) => {
  const startValue = Math.max(0, endValue - 50);
  const [count, setCount] = useState(startValue);
  const timerRef = useRef(null);

  useEffect(() => {
    // Reset count when section goes out of view
    if (!isVisible) {
      setCount(startValue);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start animation when section comes into view
    if (isVisible && !timerRef.current) {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const increment = (endValue - startValue) / steps;
      const stepDuration = duration / steps;

      let currentValue = startValue;
      let step = 0;

      timerRef.current = setInterval(() => {
        step++;
        currentValue += increment;

        if (step >= steps) {
          setCount(endValue);
          clearInterval(timerRef.current);
          timerRef.current = null;
        } else {
          setCount(Math.floor(currentValue));
        }
      }, stepDuration);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isVisible, endValue, startValue]);

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  return <>{formatNumber(count)}{suffix}</>;
};

const Hero = () => {
  const statsSectionRef = useRef(null);
  const [isStatsVisible, setIsStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsStatsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -100px 0px' // Trigger slightly before it fully enters viewport
      }
    );

    if (statsSectionRef.current) {
      observer.observe(statsSectionRef.current);
    }

    return () => {
      if (statsSectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div className="hero-container">
      <div className="hero-main-image">
        <img src="/main.jpeg" alt="India's Fastest House Help Service App" />
        <a
          href="https://app.proxhelp.com/"
          className="hero-click-overlay"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download App"
        ></a>
      </div>

      <div className="stats-section" ref={statsSectionRef}>
        <div className="stats-container">
          <h2 className="stats-heading">House help verified professionals available</h2>
          <div className="stats-tagline">
            <p>No more planning around your house help.</p>
            <p>Our team of verified Proxhelp Professionals are always on time.</p>
          </div>
          {/* <div className="stats-cards">
            <div className="stat-card">
              <div className="stat-number">
                <Counter endValue={100000} suffix="+" isVisible={isStatsVisible} />
              </div>
              <div className="stat-label">Homes cleaned</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                <Counter endValue={85000} suffix="+" isVisible={isStatsVisible} />
              </div>
              <div className="stat-label">Hours saved</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                <Counter endValue={1000} suffix="+" isVisible={isStatsVisible} />
              </div>
              <div className="stat-label">Proxhelp professionals</div>
            </div>
          </div> */}
        </div>
      </div>
      <div className="join-section">
        <div className="join-container">
          <div className="join-cards">
            <div className="join-card join-user">
              <div className="join-image-wrapper">
                <img src="./user.jpeg" alt="Join as a user" className="join-image" onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400/1E6BFF/FFFFFF?text=Join+as+User'; }} />
              </div>
              <div className="join-content">
                <h2 className="join-title">Join as a User</h2>
                <p className="join-description">
                  Get instant access to professional cleaning services. Book, schedule, and manage your home cleaning needs with ease.
                </p>
                <a href="https://app.proxhelp.com/" className="join-button join-button-user" target="_blank" rel="noopener noreferrer">
                  Get Started
                </a>
              </div>
            </div>

            <div className="join-card join-provider">
              <div className="join-image-wrapper">
                <img src="./provider.png" alt="Join as a provider" className="join-image" onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400/1E6BFF/FFFFFF?text=Join+as+Provider'; }} />
              </div>
              <div className="join-content">
                <h2 className="join-title">Become a ProxHelp Partner</h2>
                <p className="join-description">
                  Become a ProxHelp Professional Service Provider Partner!
                  Join ProxHelp community and earn
                  up to ₹30,000/ per Month.
                </p>
                <a href="https://provider.proxhelp.com/" className="join-button join-button-provider" target="_blank" rel="noopener noreferrer">
                  Become a Provider
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div id="services1" className="services-section">
        <div className="services-container">
          <div className="services-tag">
            <span className="services-tag-icon">★</span>
            <span>Our Services</span>
          </div>
          <h2 className="services-heading">Book trusted cleaning help</h2>
          <p className="services-subheading">From deep cleans to daily upkeep, Proxhelp's got you covered</p>

          <div className="services-marquee-wrapper">
            <div className="services-marquee">
              <div className="services-marquee-content">
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./DeepCleaning.jpeg" alt="Deep cleaning service" />
                  </div>
                  <div className="service-label">Deep Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./BathroomCleaning.jpeg" alt="Bathroom cleaning service" />
                  </div>
                  <div className="service-label">Bathroom Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./KitchenCleaning.jpeg" alt="Kitchen cleaning service" />
                  </div>
                  <div className="service-label">Kitchen Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./Dusting.jpeg" alt="Home cleaning service" />
                  </div>
                  <div className="service-label">Dusting</div>
                </div>


                <div className="service-item">
                  <div className="service-image-card">
                    <img src=".\BeautyService.jpeg" alt="Bathroom cleaning service" />
                  </div>
                  <div className="service-label">Beauty Service</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./DeepCleaning.jpeg" alt="Deep cleaning service" />
                  </div>
                  <div className="service-label">Deep Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./BathroomCleaning.jpeg" alt="Bathroom cleaning service" />
                  </div>
                  <div className="service-label">Bathroom Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./KitchenCleaning.jpeg" alt="Kitchen cleaning service" />
                  </div>
                  <div className="service-label">Kitchen Cleaning</div>
                </div>
                <div className="service-item">
                  <div className="service-image-card">
                    <img src="./Dusting.jpeg" alt="Home cleaning service" />
                  </div>
                  <div className="service-label">Dusting</div>
                </div>


                <div className="service-item">
                  <div className="service-image-card">
                    <img src=".\BeautyService.jpeg" alt="Bathroom cleaning service" />
                  </div>
                  <div className="service-label">Beauty Service</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div id="how-it-works" className="how-it-works-section">
        <div className="how-it-works-container">
          <div className="how-it-works-tag">
            <span className="how-it-works-tag-icon">💡</span>
            <span>How it works</span>
          </div>
          <h2 className="how-it-works-heading">Simple steps to a cleaner home</h2>
          <p className="how-it-works-subheading">Follow these simple steps to get lightning-fast household help.</p>

          <div className="steps-container">
            <div className="step-card">
              <div className="step-badge">STEP 1</div>
              <h3 className="step-title">Pick from a range of househelp services</h3>
              <div className="step-phone-container">
                <img src="/image1.png" alt="Step 1 - Pick services" className="step-phone-image" />
              </div>
            </div>

            <div className="step-card">
              <div className="step-badge">STEP 2</div>
              <h3 className="step-title">Add the service into your cart</h3>
              <div className="step-phone-container">
                <img src="/image2.png" alt="Step 2 - Add to cart" className="step-phone-image" />
              </div>
            </div>

            <div className="step-card">
              <div className="step-badge">STEP 3</div>
              <h3 className="step-title">Choose instant, scheduled, or recurring. Pay & done!</h3>
              <div className="step-phone-container">
                <img src="/image3.png" alt="Step 3 - Pay and done" className="step-phone-image" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="testimonials" className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-tag">
            <span className="testimonials-tag-icon">😊</span>
            <span>Our Testimonials</span>
          </div>
          <h2 className="testimonials-heading">User reviews and feedback</h2>
          <p className="testimonials-subheading">See how Proxhelp has transformed users' experiences through their own words</p>

          <div className="testimonials-marquee-wrapper">
            <div className="testimonials-marquee-top">
              <div className="testimonials-marquee-content-top">
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="Neha" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Neha</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">The service was simple and effective. It met my expectations without any hassle. Good overall experience.</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="Pradnyesh" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Pradnyesh</div>
                      <div className="testimonial-location">Suncity</div>
                    </div>
                    <p className="testimonial-text">Great work, my home was left spotless and fresh. The cleaning was thorough, and I appreciated the attention to detail. I'll recommend it. 👍</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" alt="Ridhi Saluja" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Ridhi Saluja</div>
                      <div className="testimonial-location">Sector 56</div>
                    </div>
                    <p className="testimonial-text">The services have definitely improved from the first time. Preferences are kept as top priority. Thank you for making our lives easier with Proxhelp!</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="Neha" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Neha</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">The service was simple and effective. It met my expectations without any hassle. Good overall experience.</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="Pradnyesh" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Pradnyesh</div>
                      <div className="testimonial-location">Suncity</div>
                    </div>
                    <p className="testimonial-text">Great work, my home was left spotless and fresh. The cleaning was thorough, and I appreciated the attention to detail. I'll recommend it. 👍</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" alt="Ridhi Saluja" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Ridhi Saluja</div>
                      <div className="testimonial-location">Sector 56</div>
                    </div>
                    <p className="testimonial-text">The services have definitely improved from the first time. Preferences are kept as top priority. Thank you for making our lives easier with Proxhelp!</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="testimonials-marquee-bottom">
              <div className="testimonials-marquee-content-bottom">
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop" alt="Ritika" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Ritika</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">Seamless experience from booking to completion. The staff was courteous, punctual, and did a fantastic job.</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop" alt="Sameer" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Sameer</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">Really liked your service, it was smooth, efficient, and just what I needed. Would definitely recommend to others. ☀️</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop" alt="Karishma" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Karishma</div>
                      <div className="testimonial-location">Suncity</div>
                    </div>
                    <p className="testimonial-text">Absolutely excellent service. The team was prompt and professional throughout. Would love to use it again.</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop" alt="Ritika" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Ritika</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">Seamless experience from booking to completion. The staff was courteous, punctual, and did a fantastic job.</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop" alt="Sameer" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Sameer</div>
                      <div className="testimonial-location">Sector 57</div>
                    </div>
                    <p className="testimonial-text">Really liked your service, it was smooth, efficient, and just what I needed. Would definitely recommend to others. ☀️</p>
                  </div>
                </div>
                <div className="testimonial-card">
                  <div className="testimonial-quote">"</div>
                  <div className="testimonial-content">
                    <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop" alt="Karishma" className="testimonial-avatar" />
                    <div className="testimonial-info">
                      <div className="testimonial-name">Karishma</div>
                      <div className="testimonial-location">Suncity</div>
                    </div>
                    <p className="testimonial-text">Absolutely excellent service. The team was prompt and professional throughout. Would love to use it again.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <div id="faq" className="faq-section">
        <div className="faq-container">
          <div className="faq-tag">
            <span className="faq-tag-icon">?</span>
            <span>FAQ's</span>
          </div>
          <h2 className="faq-heading">Frequently Asked Questions</h2>

          <div className="faq-list">
            <FAQItem
              question="What is Proxhelp?"
              answer="Proxhelp is India's fastest house help service app that connects you with verified professionals for all your household cleaning needs."
            />
            <FAQItem
              question="How do I book services?"
              answer="Simply download the Proxhelp app, browse through our range of services, add them to your cart, and choose instant, scheduled, or recurring booking options. Pay and you're done!"
            />
            <FAQItem
              question="Can I book a recurring service?"
              answer="Yes! Proxhelp offers recurring service options. You can set up daily, weekly, or monthly recurring bookings for your convenience."
            />
            <FAQItem
              question="How can I trust your service?"
              answer="All Proxhelp professionals are verified and background-checked. We maintain high quality standards excellent ratings."
            />
            <FAQItem
              question="How are your services priced?"
              answer="Our services are transparently priced with competitive rates. Prices vary based on the type of service and location. You can see all pricing details in the app before booking."
            />
            <FAQItem
              question="Where can I use Proxhelp?"
              answer="Proxhelp is currently available in parts of Delhi and Delhi NCR. We're expanding to more cities soon!"
            />
            {/* <FAQItem
              question="How do I contact support?"
              answer="You can contact our support team through the Proxhelp app, or email us at help@withProxhelp.com. Our team is available 24/7 to assist you with any queries or concerns."
            /> */}
          </div>
        </div>
      </div>
      {/* <div className="download-section">
        <div className="download-container">
          <div className="download-phones-wrapper"> */}
      {/* <div className="download-phone-left">
              <img src="/image4.png" alt="Proxhelp app - Kitchen prep" />
            </div> */}
      {/* <div className="download-phone-center">
              <div className="download-phone-bg">
                <img src="/image.png" alt="Background pattern" className="download-bg-image" />
              </div>
              <img src="/image4.png" alt="Proxhelp app - Utensils cleaning" className="download-phone-main" />
            </div> */}
      {/* <div className="download-phone-right">
              <img src="/image4.png" alt="Proxhelp app - Cleaning service" />
            </div> */}
      {/* </div> */}

      {/* <div className="download-text-container">
            <h2 className="download-heading">
              Get expert house help in minutes.<br />
              Download Proxhelp!
            </h2>
            <p className="download-subheading">
              Thousands already trust us for hassle-free home upkeep.
            </p>
          </div> */}

      {/* <div className="download-buttons-container">
            <a 
              href="https://play.google.com/store/apps/details?id=com.company.Proxhelp" 
              target="_blank" 
              rel="noopener"
              className="download-button google-play-button"
            >
              <img 
                src="https://framerusercontent.com/images/3P1ckGuQQEInpODdTv3kJOEgnYQ.png?scale-down-to=512&width=536&height=160" 
                alt="Get it on Google Play"
              />
            </a>
            <a 
              href="https://apps.apple.com/in/app/Proxhelp-10-min-home-services/id6743402816" 
              target="_blank" 
              rel="noopener"
              className="download-button app-store-button"
            >
              <img 
                src="https://framerusercontent.com/images/XFHvXmLh07GYeJbajNiemQLI9MY.png?width=510&height=160" 
                alt="Download on the App Store"
              />
            </a>
          </div> */}
    </div>
    //   </div>
    // </div>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-question-text">{question}</span>
        <button className="faq-toggle" aria-label="Toggle answer">
          <span className={`faq-icon ${isOpen ? 'rotate' : ''}`}>+</span>
        </button>
      </div>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

export default Hero;

